import { createTheme, defaultTheme } from '@mui/material/styles';
import { useTheme, useMediaQuery } from '@mui/material';
import { green, purple } from '@mui/material/colors';

// https://mui.com/material-ui/customization/default-theme
export const karyakramaTheme = createTheme({
  // https://mui.com/material-ui/customization/breakpoints/
  // https://gs.statcounter.com/screen-resolution-stats
  // https://www.browserstack.com/guide/common-screen-resolutions
  breakpoints: {
    values: {
      xs: 0,      // FALLBACK: Old mobile phones
      sm: 360,    // MOBILE: Most of the mobile phones
      md: 768,    // TABLET: Most of the tablets and most of the mobile phones in landscape mode
      lg: 1024,   // LAPTOP: Old laptops and desktops and most of the tablets in landscape mode
      xl: 1440,   // HD-LAPTOP: Basic laptops and desktops
      xxl: 1920,  // FHD-LAPTOP: Modern laptops and desktops
    },
  },
  palette: {
    background: {
      // Color used for background of the application
      default: '#ffffff',
      paper: '#ffffff',
      light: '#b3b3b3',
      dark: '#808080',
    },
    primary: {
      // Color used for primary buttons and active elements
      main: '#233836',
      light: '#375754',
      dark: '#0f1918',
      contrastText: '#ffffff',
    },
    secondary: {
      // Color used for secondary actions or elements
      main: '#808080',
      light: '#b3b3b3',
      dark: '#4d4d4d',
      contrastText: '#000',
    },
    error: {
      // Color used for error messages or alerts
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
      contrastText: '#ffffff',
    },
    warning: {
      // Color used for warnings
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
      contrastText: '#ffffff',
    },
    info: {
      // Color used for informational messages
      main: '#0288d1',
      light: '#03a9f4',
      dark: '#01579b',
      contrastText: '#ffffff',
    },
    success: {
      // Color used for success messages
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
      contrastText: '#ffffff',
    },
    text: {
      // Color used for text
      primary: '#000000',
      secondary: '#333333',
      disabled: '#888888',
    },
    contrastAppBar: {
      // Color used for success messages
      main: '#ffffff',
      light: '#ffffff',
      dark: '#000000',
      contrastText: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", "sans-serif"',
    htmlFontSize: 14,
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '1.4rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.0rem',
      fontWeight: 400,
    },
    h5: {
      fontSize: '0.87rem',
      fontWeight: 400,
    },
    h6: {
      fontSize: '0.75rem',
      fontWeight: 400,
    },
    body1: {
      fontSize: '0.87rem',
      fontWeight: 300,
    },
    body2: {
      fontSize: '0.75rem',
      fontWeight: 300,
    },
    body3: {
      fontSize: '0.50rem',
      fontWeight: 200,
    },
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      textTransform: 'uppercase',
    },
    MuiTextField: {
      fontWeight: 400,
      fontSize: '0.875rem',
    },
  },
  components: {
    MuiToolbar: {
      styleOverrides: {
        root: {
          paddingLeft: '16px',
          paddingRight: '16px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          minWidth: 'sm',
          maxWidth: 'lg',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.0rem', // 34px for h4 size
          fontWeight: 400, // Regular weight for the title
          lineHeight: 1.5, // Standard line height
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: '40px', // With padding mostly we can get touch target of 48
          height: 'auto', // Allows flexibility for larger screens
          paddingTop: '4px', // Adjust padding to ensure the effective height is 40px
          paddingBottom: '4px', // Adjust padding
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)', // Custom hover color
            borderRadius: '8px', // Ensure hover effect has rounded corners
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            // color: '#fff',
            borderRadius: '8px',
          },
          '&.active': {
            color: 'primary.main',
            fontWeight: 'bold',
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: '8px',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '48px', // Decrease default width
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.875rem', // Smaller font
          fontWeight: '400', // Normal font weight
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // Small font size for table content
          fontWeight: '400', // Normal font weight
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          '&.Mui-active': { color: '#233836' }, // Highlight active column
          '& .MuiTableSortLabel-icon': { color: '#233836 !important' }, // Ensure arrow is visible
          '&:hover': { color: '#375754' }, // Highlight active column
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          minWidth: '80px',
          paddingLeft: '2px',
          paddingRight: '2px',
          paddingTop: '2px',
          paddingBottom: '2px',
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          minWidth: '100px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          minWidth: '100px',
          fontSize: '0.875rem', // Small font size for table content
          fontWeight: '400', // Normal font weight
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // Small font size for table content
          fontWeight: '400', // Normal font weight
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)', // Custom hover color
            borderRadius: '8px', // Ensure hover effect has rounded corners
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          minWidth: '100px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          minWidth: '100px',
          fontSize: '0.875rem', // Small font size for table content
          fontWeight: '400', // Normal font weight
          height: '100%', // match MUI button height
          '& .MuiOutlinedInput-root': {
            height: '100%', // match MUI button height
          }
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // Disable uppercase text for all buttons
          '&:hover': {
            backgroundColor: '#548681', // Custom hover color
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'lightgrey',
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 200, // Delays the appearance
        // leaveDelay: 50, // Delays hiding
        arrow: true, // Adds a small arrow toward the element
      },
    },
  },
});

// Check if device screen is small/medium - Phone/Tab
export const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("lg")); // < less than
};

export default karyakramaTheme;
